import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, User, X, Home, Clock, Settings as SettingsIcon, Globe, Rss, LogOut, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const menuItems = [
  { to: '/', icon: Home, label: 'Accueil', testid: 'menu-accueil' },
  { to: '/history', icon: Clock, label: 'Historique', testid: 'menu-historique' },
  { to: '/settings', icon: SettingsIcon, label: 'Réglages', testid: 'menu-reglages' },
  { to: '/settings/sites', icon: Globe, label: 'Médias', testid: 'menu-sites' },
  { to: '/settings/feeds', icon: Rss, label: 'Flux RSS', testid: 'menu-feeds' },
];

const adminItem = { to: '/admin', icon: Shield, label: 'Admin', testid: 'menu-admin' };

const BrandMark = ({ size = 28 }) => (
  <img
    src="/brand/autoradko-logo.png"
    alt="Auto Radko"
    className="select-none"
    style={{ height: size, width: 'auto', maxHeight: 44 }}
    draggable={false}
  />
);

export default function TopBar({ onSearch }) {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const close = () => setOpen(false);
  const doLogout = () => { logout(); nav('/login'); };

  return (
    <>
      <header
        className="sticky top-0 z-40 relative flex items-center justify-between px-3 py-1.5 shadow-sm"
        style={{ background: 'linear-gradient(90deg, #1E3FBE 0%, #2563EB 35%, #C2490F 75%, #F97316 100%)' }}
        data-testid="top-bar"
      >
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-open-menu"
          className="p-2 rounded-lg text-white hover:bg-white/15 active:scale-95 transition"
          aria-label="Ouvrir le menu"
        >
          <Menu size={24} />
        </button>

        <button
          onClick={() => nav('/')}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center active:scale-95 transition"
          data-testid="topbar-logo"
          aria-label="Retour à l'accueil"
        >
          <img
            src="/brand/autoradko-logo.png"
            alt="Auto Radko"
            className="h-12 w-auto select-none"
            draggable={false}
          />
        </button>

        <div className="flex items-center gap-1">
          {onSearch && (
            <button
              onClick={() => setSearchOpen((s) => !s)}
              data-testid="btn-search"
              className="p-2 rounded-lg text-white hover:bg-white/15 active:scale-95 transition"
              aria-label="Rechercher"
            >
              <Search size={22} />
            </button>
          )}
          <button
            onClick={() => setProfileOpen(true)}
            data-testid="btn-profile"
            className="p-2 rounded-lg text-white hover:bg-white/15 active:scale-95 transition"
            aria-label="Profil"
          >
            <User size={22} />
          </button>
        </div>
      </header>

      {searchOpen && onSearch && (
        <div className="sticky top-[56px] z-30 bg-white border-b border-slate-100 px-4 py-2 fade-in-up">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); onSearch(e.target.value); }}
            placeholder="Rechercher un sujet…"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none text-sm"
            data-testid="search-input"
          />
        </div>
      )}

      {/* Hamburger Drawer */}
      {open && (
        <div className="fixed inset-0 z-[60]" data-testid="menu-drawer">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={close}
          />
          <aside className="absolute top-0 left-0 h-full w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col fade-in-up">
            <div
              className="flex items-center justify-between p-4"
              style={{ background: 'linear-gradient(90deg, #1E3FBE 0%, #F97316 100%)' }}
            >
              <BrandMark size={32} />
              <button onClick={close} data-testid="btn-close-menu" className="p-1.5 rounded-lg text-white hover:bg-white/15">
                <X size={22} />
              </button>
            </div>
            {user && (
              <div className="px-5 py-3 border-b border-slate-100">
                <div className="text-sm font-semibold text-slate-900 truncate">{user.name || user.email}</div>
                <div className="text-xs text-slate-500 truncate">{user.email}</div>
              </div>
            )}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {menuItems.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.to}
                    onClick={() => { close(); nav(m.to); }}
                    data-testid={m.testid}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-100 active:scale-[0.98] transition text-left"
                  >
                    <Icon size={20} className="text-blue-600" />
                    <span className="font-medium text-slate-800">{m.label}</span>
                  </button>
                );
              })}
            </nav>
            <div className="p-3 border-t border-slate-100">
              <button
                onClick={() => { close(); doLogout(); }}
                data-testid="menu-logout"
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-red-50 text-red-600 transition text-left"
              >
                <LogOut size={20} />
                <span className="font-medium">Déconnexion</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Profile Popover */}
      {profileOpen && (
        <div className="fixed inset-0 z-[60]" data-testid="profile-sheet">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setProfileOpen(false)} />
          <div className="absolute top-[60px] right-3 w-60 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden fade-in-up">
            <div className="p-4 border-b border-slate-100">
              <div className="font-semibold text-slate-900 truncate" data-testid="profile-name">{user?.name || 'Utilisateur'}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => { setProfileOpen(false); nav('/settings'); }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              data-testid="profile-settings"
            >
              <SettingsIcon size={16} /> Réglages
            </button>
            <button
              onClick={() => { setProfileOpen(false); doLogout(); }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2"
              data-testid="profile-logout"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      )}
    </>
  );
}
